/**
 * 编辑器单 store。
 * - doc 通过 immer produceWithPatches 修改，patches/inversePatches 进历史栈。
 * - beginBatch/endBatch 之间的多次修改合并为一条历史（拖拽用）。
 * - UI 状态（选中、当前页、缩放等）不进历史。
 */
import { create } from 'zustand';
import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import {
  createLayer, createPage, createWork, genId,
  type Layer, type Page, type Rect, type Work,
} from '@h5/schema';

enablePatches();

const HISTORY_LIMIT = 100;

export interface HistoryEntry {
  patches: Patch[];
  inversePatches: Patch[];
}

export type LeftTab = 'materials' | 'pages' | 'layers' | 'design';
export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export interface EditorUI {
  selectedIds: string[];
  currentPageId: string;
  zoom: number;
  leftTab: LeftTab;
  saveStatus: SaveStatus;
  previewOpen: boolean;
  /** 服务端作品 id，未创建成功时为 null（离线模式） */
  remoteId: string | null;
}

export interface EditorState {
  doc: Work;
  past: HistoryEntry[];
  future: HistoryEntry[];
  /** 非 null 表示正在批量修改 */
  batch: HistoryEntry | null;
  ui: EditorUI;

  // ---- 文档 ----
  setDoc(doc: Work, opts?: { resetHistory?: boolean }): void;
  setTitle(title: string): void;
  setShare(patch: Partial<Work['globals']['share']>): void;
  // ---- 图层 ----
  addLayer(type: string, partial?: Partial<Layer>, pageId?: string): string;
  updateLayer(id: string, patch: Partial<Omit<Layer, 'id' | 'props'>>): void;
  updateLayerProps(id: string, patch: Record<string, unknown>): void;
  moveLayer(id: string, rect: Partial<Rect>): void;
  moveLayers(ids: string[], dx: number, dy: number): void;
  removeLayers(ids: string[]): void;
  duplicateLayers(ids: string[]): string[];
  /** 调整 zIndex：'up' | 'down' | 'top' | 'bottom' 或直接给数值 */
  reorderLayer(id: string, dir: 'up' | 'down' | 'top' | 'bottom' | number): void;
  pasteLayers(layers: Layer[]): string[];
  // ---- 页面 ----
  addPage(partial?: Partial<Page>, index?: number): string;
  removePage(id: string): void;
  duplicatePage(id: string): string;
  movePage(from: number, to: number): void;
  setPageProps(id: string, patch: Partial<Omit<Page, 'id' | 'layers'>>): void;
  // ---- 历史 ----
  undo(): void;
  redo(): void;
  beginBatch(): void;
  endBatch(): void;
  // ---- UI ----
  select(ids: string[], opts?: { toggle?: boolean }): void;
  clearSelection(): void;
  setCurrentPage(id: string): void;
  setZoom(zoom: number): void;
  setLeftTab(tab: LeftTab): void;
  setSaveStatus(s: SaveStatus): void;
  setPreviewOpen(open: boolean): void;
  setRemoteId(id: string | null): void;
}

/** 在 pages 与 popups 中查找页面 */
export function findPage(doc: Work, pageId: string): Page | undefined {
  return doc.pages.find((p) => p.id === pageId) ?? doc.popups.find((p) => p.id === pageId);
}

export function findLayer(doc: Work, layerId: string): { page: Page; layer: Layer } | undefined {
  for (const page of [...doc.pages, ...doc.popups]) {
    const layer = page.layers.find((l) => l.id === layerId);
    if (layer) return { page, layer };
  }
  return undefined;
}

function cloneLayerWithNewId(layer: Layer, offset = 0): Layer {
  const copy: Layer = JSON.parse(JSON.stringify(layer));
  copy.id = genId('l');
  copy.rect = { ...copy.rect, x: copy.rect.x + offset, y: copy.rect.y + offset };
  return copy;
}

export const useEditorStore = create<EditorState>()((set, get) => {
  /** 核心：immer 修改 doc 并记录历史 */
  const mutate = (recipe: (doc: Work) => void) => {
    const state = get();
    const [next, patches, inversePatches] = produceWithPatches(state.doc, recipe);
    if (patches.length === 0) return;
    if (state.batch) {
      // 批量：正向追加，逆向前插（撤销时后发生的先回退）
      set({
        doc: next,
        batch: {
          patches: [...state.batch.patches, ...patches],
          inversePatches: [...inversePatches, ...state.batch.inversePatches],
        },
      });
      return;
    }
    const past = [...state.past, { patches, inversePatches }];
    if (past.length > HISTORY_LIMIT) past.shift();
    set({ doc: next, past, future: [], ui: { ...state.ui, saveStatus: 'dirty' } });
  };

  const initial = createWork();

  return {
    doc: initial,
    past: [],
    future: [],
    batch: null,
    ui: {
      selectedIds: [],
      currentPageId: initial.pages[0]!.id,
      zoom: 1,
      leftTab: 'materials',
      saveStatus: 'idle',
      previewOpen: false,
      remoteId: null,
    },

    setDoc(doc, opts) {
      const firstPage = doc.pages[0]!.id;
      set((s) => ({
        doc,
        past: opts?.resetHistory === false ? s.past : [],
        future: opts?.resetHistory === false ? s.future : [],
        batch: null,
        ui: {
          ...s.ui,
          selectedIds: [],
          currentPageId: findPage(doc, s.ui.currentPageId) ? s.ui.currentPageId : firstPage,
        },
      }));
    },

    setTitle(title) {
      mutate((d) => { d.title = title; });
    },

    setShare(patch) {
      mutate((d) => { d.globals.share = { ...d.globals.share, ...patch }; });
    },

    addLayer(type, partial, pageId) {
      const targetPage = pageId ?? get().ui.currentPageId;
      const layer = createLayer(type, partial);
      mutate((d) => {
        const page = findPage(d, targetPage);
        if (!page) return;
        const maxZ = page.layers.reduce((m, l) => Math.max(m, l.style.zIndex), 0);
        layer.style.zIndex = page.layers.length ? maxZ + 1 : 0;
        page.layers.push(layer);
      });
      set((s) => ({ ui: { ...s.ui, selectedIds: [layer.id] } }));
      return layer.id;
    },

    updateLayer(id, patch) {
      mutate((d) => {
        const hit = findLayer(d, id);
        if (!hit) return;
        Object.assign(hit.layer, patch);
      });
    },

    updateLayerProps(id, patch) {
      mutate((d) => {
        const hit = findLayer(d, id);
        if (!hit) return;
        hit.layer.props = { ...hit.layer.props, ...patch };
      });
    },

    moveLayer(id, rect) {
      mutate((d) => {
        const hit = findLayer(d, id);
        if (!hit) return;
        Object.assign(hit.layer.rect, rect);
      });
    },

    moveLayers(ids, dx, dy) {
      mutate((d) => {
        for (const id of ids) {
          const hit = findLayer(d, id);
          if (!hit || hit.layer.locked) continue;
          hit.layer.rect.x += dx;
          hit.layer.rect.y += dy;
        }
      });
    },

    removeLayers(ids) {
      const setIds = new Set(ids);
      mutate((d) => {
        for (const page of [...d.pages, ...d.popups]) {
          page.layers = page.layers.filter((l) => !setIds.has(l.id));
        }
      });
      set((s) => ({ ui: { ...s.ui, selectedIds: s.ui.selectedIds.filter((i) => !setIds.has(i)) } }));
    },

    duplicateLayers(ids) {
      const newIds: string[] = [];
      mutate((d) => {
        for (const id of ids) {
          const hit = findLayer(d, id);
          if (!hit) continue;
          const copy = cloneLayerWithNewId(hit.layer, 10);
          copy.style.zIndex = hit.page.layers.reduce((m, l) => Math.max(m, l.style.zIndex), 0) + 1;
          hit.page.layers.push(copy);
          newIds.push(copy.id);
        }
      });
      if (newIds.length) set((s) => ({ ui: { ...s.ui, selectedIds: newIds } }));
      return newIds;
    },

    pasteLayers(layers) {
      const newIds: string[] = [];
      const targetPage = get().ui.currentPageId;
      mutate((d) => {
        const page = findPage(d, targetPage);
        if (!page) return;
        let z = page.layers.reduce((m, l) => Math.max(m, l.style.zIndex), 0);
        for (const l of layers) {
          const copy = cloneLayerWithNewId(l, 10);
          copy.style.zIndex = ++z;
          page.layers.push(copy);
          newIds.push(copy.id);
        }
      });
      if (newIds.length) set((s) => ({ ui: { ...s.ui, selectedIds: newIds } }));
      return newIds;
    },

    reorderLayer(id, dir) {
      mutate((d) => {
        const hit = findLayer(d, id);
        if (!hit) return;
        const { page, layer } = hit;
        if (typeof dir === 'number') { layer.style.zIndex = dir; return; }
        const sorted = [...page.layers].sort((a, b) => a.style.zIndex - b.style.zIndex);
        const idx = sorted.findIndex((l) => l.id === id);
        let target = idx;
        if (dir === 'up') target = Math.min(sorted.length - 1, idx + 1);
        if (dir === 'down') target = Math.max(0, idx - 1);
        if (dir === 'top') target = sorted.length - 1;
        if (dir === 'bottom') target = 0;
        if (target === idx) return;
        sorted.splice(idx, 1);
        sorted.splice(target, 0, layer);
        // 重新压实 zIndex，避免碰撞
        sorted.forEach((l, i) => { l.style.zIndex = i; });
      });
    },

    addPage(partial, index) {
      const page = createPage({ name: `第 ${get().doc.pages.length + 1} 页`, ...partial });
      mutate((d) => {
        if (index === undefined || index < 0 || index > d.pages.length) d.pages.push(page);
        else d.pages.splice(index, 0, page);
      });
      set((s) => ({ ui: { ...s.ui, currentPageId: page.id, selectedIds: [] } }));
      return page.id;
    },

    removePage(id) {
      const { doc, ui } = get();
      if (doc.pages.length <= 1 && doc.pages.some((p) => p.id === id)) return; // 至少保留一页
      const idx = doc.pages.findIndex((p) => p.id === id);
      mutate((d) => {
        d.pages = d.pages.filter((p) => p.id !== id);
        d.popups = d.popups.filter((p) => p.id !== id);
      });
      if (ui.currentPageId === id) {
        const pages = get().doc.pages;
        const next = pages[Math.min(Math.max(0, idx), pages.length - 1)]!;
        set((s) => ({ ui: { ...s.ui, currentPageId: next.id, selectedIds: [] } }));
      }
    },

    duplicatePage(id) {
      let newId = '';
      mutate((d) => {
        const idx = d.pages.findIndex((p) => p.id === id);
        if (idx < 0) return;
        const src = d.pages[idx]!;
        const copy: Page = JSON.parse(JSON.stringify(src));
        copy.id = genId('p');
        copy.name = `${src.name || '页面'} 副本`;
        copy.layers = copy.layers.map((l) => ({ ...l, id: genId('l') }));
        d.pages.splice(idx + 1, 0, copy);
        newId = copy.id;
      });
      if (newId) set((s) => ({ ui: { ...s.ui, currentPageId: newId, selectedIds: [] } }));
      return newId;
    },

    movePage(from, to) {
      mutate((d) => {
        if (from < 0 || from >= d.pages.length || to < 0 || to >= d.pages.length || from === to) return;
        const [p] = d.pages.splice(from, 1);
        d.pages.splice(to, 0, p!);
      });
    },

    setPageProps(id, patch) {
      mutate((d) => {
        const page = findPage(d, id);
        if (!page) return;
        Object.assign(page, patch);
      });
    },

    undo() {
      const s = get();
      if (s.batch) return;
      const entry = s.past[s.past.length - 1];
      if (!entry) return;
      const doc = applyPatches(s.doc, entry.inversePatches);
      set({
        doc,
        past: s.past.slice(0, -1),
        future: [entry, ...s.future],
        ui: {
          ...s.ui,
          saveStatus: 'dirty',
          selectedIds: s.ui.selectedIds.filter((id) => findLayer(doc, id)),
          currentPageId: findPage(doc, s.ui.currentPageId) ? s.ui.currentPageId : doc.pages[0]!.id,
        },
      });
    },

    redo() {
      const s = get();
      if (s.batch) return;
      const entry = s.future[0];
      if (!entry) return;
      const doc = applyPatches(s.doc, entry.patches);
      set({
        doc,
        past: [...s.past, entry],
        future: s.future.slice(1),
        ui: {
          ...s.ui,
          saveStatus: 'dirty',
          selectedIds: s.ui.selectedIds.filter((id) => findLayer(doc, id)),
          currentPageId: findPage(doc, s.ui.currentPageId) ? s.ui.currentPageId : doc.pages[0]!.id,
        },
      });
    },

    beginBatch() {
      if (get().batch) return;
      set({ batch: { patches: [], inversePatches: [] } });
    },

    endBatch() {
      const s = get();
      if (!s.batch) return;
      if (s.batch.patches.length === 0) { set({ batch: null }); return; }
      const past = [...s.past, s.batch];
      if (past.length > HISTORY_LIMIT) past.shift();
      set({ batch: null, past, future: [], ui: { ...s.ui, saveStatus: 'dirty' } });
    },

    select(ids, opts) {
      set((s) => {
        if (!opts?.toggle) return { ui: { ...s.ui, selectedIds: ids } };
        const cur = new Set(s.ui.selectedIds);
        for (const id of ids) cur.has(id) ? cur.delete(id) : cur.add(id);
        return { ui: { ...s.ui, selectedIds: [...cur] } };
      });
    },
    clearSelection() { set((s) => ({ ui: { ...s.ui, selectedIds: [] } })); },
    setCurrentPage(id) { set((s) => ({ ui: { ...s.ui, currentPageId: id, selectedIds: [] } })); },
    setZoom(zoom) { set((s) => ({ ui: { ...s.ui, zoom: Math.min(3, Math.max(0.2, zoom)) } })); },
    setLeftTab(tab) { set((s) => ({ ui: { ...s.ui, leftTab: tab } })); },
    setSaveStatus(saveStatus) { set((s) => ({ ui: { ...s.ui, saveStatus } })); },
    setPreviewOpen(previewOpen) { set((s) => ({ ui: { ...s.ui, previewOpen } })); },
    setRemoteId(remoteId) { set((s) => ({ ui: { ...s.ui, remoteId } })); },
  };
});

// ---------- 选择器 ----------
export const selectCurrentPage = (s: EditorState) => findPage(s.doc, s.ui.currentPageId);
export const selectSelectedLayers = (s: EditorState): Layer[] => {
  const page = findPage(s.doc, s.ui.currentPageId);
  if (!page) return [];
  return page.layers.filter((l) => s.ui.selectedIds.includes(l.id));
};
export const selectCanUndo = (s: EditorState) => s.past.length > 0 && !s.batch;
export const selectCanRedo = (s: EditorState) => s.future.length > 0 && !s.batch;
